import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, Play, RefreshCw, LoaderCircle } from "lucide-react";
import Hls from "hls.js";

import Heading from "../../../../components/typography/Heading";
import Text from "../../../../components/typography/Text";
import Button from "../../../../components/buttons/Button";

import "./VideoPlayer.css";

const isHlsSource = (src, type) =>
  String(type).toLowerCase() === "hls" ||
  /\.m3u8(?:[?#]|$)/i.test(String(src || ""));

export default function VideoPlayer({
  src,
  poster,
  title = "Match replay",
  type = "mp4",
  onRefresh,
}) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const recoveryCountRef = useRef(0);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !src) {
      setLoading(false);
      return;
    }

    let active = true;
    let hls = null;

    setError("");
    setLoading(true);
    setPlaying(false);
    setStarted(false);

    recoveryCountRef.current = 0;

    video.pause();
    video.removeAttribute("src");
    video.load();

    const handleFatalError = (message) => {
      if (!active) return;

      setError(message);
      setLoading(false);
      setPlaying(false);
    };

    if (isHlsSource(src, type)) {
      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = src;
      } else if (Hls.isSupported()) {
        hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          capLevelToPlayerSize: true,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          backBufferLength: 30,
        });

        hlsRef.current = hls;

        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!active || !data.fatal) return;

          if (recoveryCountRef.current >= 2) {
            handleFatalError(
              "Playback could not be recovered. Please try again.",
            );
            return;
          }

          recoveryCountRef.current += 1;

          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            hls.startLoad();
            return;
          }

          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
            return;
          }

          handleFatalError("Unable to play this video. Please try again.");
        });

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (active) setLoading(false);
        });

        hls.attachMedia(video);
        hls.loadSource(src);
      } else {
        handleFatalError("This browser does not support HLS playback.");
      }
    } else {
      video.src = src;
    }

    return () => {
      active = false;

      video.pause();

      if (hls) {
        hls.destroy();
        hlsRef.current = null;
      }

      video.removeAttribute("src");
      video.load();
    };
  }, [src, type, retryKey]);

  const handlePlay = async () => {
    const video = videoRef.current;

    if (!video || error) return;

    try {
      await video.play();
    } catch (playError) {
      if (playError?.name === "AbortError") return;

      setError("Unable to start playback.");
    }
  };

  const handleRetry = async () => {
    if (refreshing) return;

    setRefreshing(true);
    setError("");
    setLoading(true);

    try {
      if (onRefresh) {
        await onRefresh();
      }

      setRetryKey((value) => value + 1);
    } catch {
      setError("Unable to refresh playback. Please try again.");
      setLoading(false);
    } finally {
      setRefreshing(false);
    }
  };

  if (!src) {
    return (
      <div className="katch-player katch-player--empty">
        <div className="katch-player__empty-content">
          <div className="katch-player__icon">
            <Play size={28} />
          </div>

          <Heading as="h3" size="sm">
            Playback unavailable
          </Heading>

          <Text as="p" size="sm" tone="secondary">
            This video is currently unavailable.
          </Text>

          {onRefresh && (
            <Button
              value="Try Again"
              variant="primary-outlined"
              icon={<RefreshCw size={16} />}
              onClick={handleRetry}
              disabled={refreshing}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="katch-player" aria-label={`${title} video player`}>
      <video
        ref={videoRef}
        className="katch-player__video"
        poster={poster || undefined}
        controls={!Boolean(error)}
        controlsList="nodownload noremoteplayback"
        disablePictureInPicture
        disableRemotePlayback
        playsInline
        preload="metadata"
        onContextMenu={(event) => event.preventDefault()}
        onDragStart={(event) => event.preventDefault()}
        onLoadedMetadata={() => setLoading(false)}
        onCanPlay={() => setLoading(false)}
        onWaiting={() => setLoading(true)}
        onPlaying={() => {
          recoveryCountRef.current = 0;
          setPlaying(true);
          setStarted(true);
          setLoading(false);
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onError={() => {
          setError("Unable to load the video.");
          setLoading(false);
          setPlaying(false);
        }}
        aria-label={title}
      />

      {!error && loading && !playing && (
        <div className="katch-player__loading" role="status">
          <LoaderCircle
            size={30}
            className="katch-player__spinner"
            aria-hidden="true"
          />

          <Text as="span" size="sm">
            Loading video...
          </Text>
        </div>
      )}

      {!error && !loading && !started && (
        <button
          type="button"
          className="katch-player__play"
          onClick={handlePlay}
          aria-label={`Play ${title}`}
        >
          <Play size={28} fill="currentColor" />
        </button>
      )}

      {error && (
        <div className="katch-player__error" role="alert">
          <div className="katch-player__error-content">
            <div className="katch-player__icon katch-player__icon--error">
              <AlertCircle size={26} />
            </div>

            <Heading as="h3" size="sm">
              Playback unavailable
            </Heading>

            <Text as="p" size="sm" tone="secondary">
              {error}
            </Text>

            <Button
              value={refreshing ? "Retrying..." : "Retry playback"}
              variant="primary-outlined"
              icon={<RefreshCw size={16} />}
              onClick={handleRetry}
              disabled={refreshing}
            />
          </div>
        </div>
      )}
    </div>
  );
}
